import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {RequestStatus} from '../../utils/Enums';
import {apiClient} from '../../utils/ApiClient';
import {Service} from "../../models/Service";
import {NewServiceFormProps} from "../../components/services/NewServiceScreen";
import {ModifyServiceFormProps} from "../../components/services/ModifyServiceScreen";

interface ServicesState {
    services: Service[];
    currentService: Service | null;
    getServicesStatus: RequestStatus;
    createServiceStatus: RequestStatus;
    updateServiceStatus: RequestStatus;
    deleteServiceStatus: RequestStatus;
    updateEmailsStatus: RequestStatus;
    getCurrentServiceStatus: RequestStatus;
    errors: string[];
}

const initialState: ServicesState = {
    services: JSON.parse(localStorage.getItem('services') || '[]'),
    currentService: null,
    getServicesStatus: RequestStatus.Idle,
    createServiceStatus: RequestStatus.Idle,
    updateServiceStatus: RequestStatus.Idle,
    deleteServiceStatus: RequestStatus.Idle,
    getCurrentServiceStatus: RequestStatus.Idle,
    updateEmailsStatus: RequestStatus.Idle,
    errors: [],
};

export const getServices = createAsyncThunk('services/getServices', async (withDeleted: boolean = false, { rejectWithValue }) => {
    let url = 'services';
    if (withDeleted) {
        url += '?withDeleted=true';
    }
    const response = await apiClient.get(url);
    return response.data;
});

export const createService = createAsyncThunk('services/createService', async (data: NewServiceFormProps, { rejectWithValue }) => {
    const response = await apiClient.post('services', data);
    return response.data;
});

export const updateService = createAsyncThunk(
    'services/updateService',
    async ({ id, data }: { id: string | undefined; data: ModifyServiceFormProps }, { rejectWithValue }) => {
        const response = await apiClient.put(`services/${id}`, data);
        return response.data;
    }
);

export const deleteService = createAsyncThunk('services/deleteService', async ({ id }: { id: string | undefined }, { rejectWithValue }) => {
    const response = await apiClient.delete(`services/${id}`);
    return response.data;
});

export const getCurrentService = createAsyncThunk(
    'services/getCurrentService',
    async ({ id }: { id: string | undefined }, { rejectWithValue }) => {
        const response = await apiClient.get(`services/${id}`);
        return response.data;
    }
);

export const updateEmails = createAsyncThunk(
    'services/emails',
    async ({ id, data }: { id: string | undefined; data: ModifyServiceFormProps }, { rejectWithValue }) => {
        const response = await apiClient.put(`services/${id}/emails`, data);
        return response.data;
    }
);

const servicesSlice = createSlice({
    name: 'services',
    initialState,
    reducers: {
        resetCreateServiceStatus: (state) => {
            state.createServiceStatus = RequestStatus.Idle;
            state.currentService = null;
        },
        resetUpdateServiceStatus: (state) => {
            state.updateServiceStatus = RequestStatus.Idle;
            state.updateEmailsStatus = RequestStatus.Idle;
            state.currentService = null;
        },
        resetDeleteServiceStatus: (state) => {
            state.deleteServiceStatus = RequestStatus.Idle;
            state.currentService = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getServices.pending, (state) => {
                state.getServicesStatus = RequestStatus.Loading;
            })
            .addCase(getServices.fulfilled, (state, action) => {
                state.getServicesStatus = RequestStatus.Success;
                state.services = action.payload;
                localStorage.setItem('services', JSON.stringify(action.payload))
            })
            .addCase(getServices.rejected, (state) => {
                state.getServicesStatus = RequestStatus.Failed;
            })

            .addCase(createService.pending, (state, action) => {
                state.createServiceStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(createService.fulfilled, (state) => {
                state.createServiceStatus = RequestStatus.Success;
            })
            .addCase(createService.rejected, (state, action)=> {
                state.createServiceStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(updateService.pending, (state, action) => {
                state.updateServiceStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(updateService.fulfilled, (state) => {
                state.updateServiceStatus = RequestStatus.Success;
            })
            .addCase(updateService.rejected, (state, action)=> {
                state.updateServiceStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(deleteService.pending, (state) => {
                state.deleteServiceStatus = RequestStatus.Loading;
            })
            .addCase(deleteService.fulfilled, (state) => {
                state.deleteServiceStatus = RequestStatus.Success;
            })
            .addCase(deleteService.rejected, (state) => {
                state.deleteServiceStatus = RequestStatus.Failed;
            })

            .addCase(getCurrentService.pending, (state) => {
                state.getCurrentServiceStatus = RequestStatus.Loading;
            })
            .addCase(getCurrentService.fulfilled, (state, action) => {
                const serviceData = action.payload;
                state.currentService = {
                    ...serviceData,
                    clientEmail: !!serviceData.client_email,
                    vendorEmail: !!serviceData.vendor_email,
                    clientContactEmail: !!serviceData.client_contact_email,
                    vendorContactEmail: !!serviceData.vendor_contact_email,
                    noAutoInvoice: !!serviceData.no_auto_invoice,
                    noValue: !!serviceData.no_value,
                    nameOnInvoice: !!serviceData.name_on_invoice,
                    railroadAndSea: !!serviceData.railroad_and_sea,
                    isCustoms: !!serviceData.is_customs,
                    isInsurance: !!serviceData.is_insurance,
                    invoiceOnLoading: !!serviceData.invoice_on_loading,
                    invoiceOnAdd: !!serviceData.invoice_on_add,
                    mailsOnAdd: !!serviceData.mails_on_add,
                };
                state.getCurrentServiceStatus = RequestStatus.Success;
            })
            .addCase(getCurrentService.rejected, (state) => {
                state.getCurrentServiceStatus = RequestStatus.Failed;
            })

            .addCase(updateEmails.pending, (state, action) => {
                state.updateEmailsStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(updateEmails.fulfilled, (state) => {
                state.updateEmailsStatus = RequestStatus.Success;
            })
            .addCase(updateEmails.rejected, (state, action)=> {
                state.updateEmailsStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            });
    },
});

export const {
    resetCreateServiceStatus,
    resetUpdateServiceStatus,
    resetDeleteServiceStatus,
} = servicesSlice.actions;
export default servicesSlice.reducer;